// src/pages/Admin-Dashboard/new-user-list-page.jsx
import React, { useEffect, useState } from "react";
import { FiUsers, FiRefreshCw, FiPlus, FiUserCheck, FiUserX } from "react-icons/fi"; 
import { useNavigate } from "react-router-dom";
import { getAllUsers, updateUserStatus, deleteUser } from "../../api/user-api";
import { ToastContainer, toast } from "react-toastify";
import UserTable from "../../components/useractivation/new-user-table";
import Sidebar from "../../components/admin-sidebar";
import Topbar from "../../components/TopbarNav";
import AddStaffModal from "../../components/AddStaffModal"; // 👈 NEW IMPORT

const UserListPage = () => {
  const navigate = useNavigate();

  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [summaryStats, setSummaryStats] = useState({
    total: 0,
    active: 0,
    deactivated: 0,
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false); // 👈 NEW STATE for Modal

  const limit = 10;

  useEffect(() => {
    const getUsers = async () => {
      setLoading(true);
      try {
        const response = await getAllUsers(page, limit, search);
        
        const users = response.users || [];
        const activeUsers = response.activeUsers || users.filter((u) => u.isActive).length;
        const deactivatedUsers = response.deactivatedUsers || users.filter((u) => !u.isActive).length;

        setUsersData(users);
        setTotalPages(response.totalPages || 1);
        setSummaryStats({
          total: response.totalUsers || users.length,
          active: activeUsers,
          deactivated: deactivatedUsers,
        });
      } catch (err) {
        console.error("Failed to load users", err);
        toast.error("Failed to load users");
        setUsersData([]);
      } finally {
        setLoading(false);
      }
    };

    getUsers();
  }, [page, limit, search]);

  const handleSearchChange = (q) => {
    setSearch(q);
    setPage(1);
  };

  const handleRowClick = (userId) => {
    // Navigate to user-profile
    navigate(`/user-profile/${userId}`);
  };

  const handleToggleStatus = async (id) => {
    try {
      const user = usersData.find((u) => u._id === id);
      const newStatus = !user.isActive;

      await updateUserStatus(id, newStatus);

      setUsersData((prev) =>
        prev.map((u) =>
          u._id === id ? { ...u, isActive: newStatus } : u
        )
      );
      // Update local stats
      setSummaryStats(prev => ({
        ...prev,
        active: newStatus ? prev.active + 1 : prev.active - 1,
        deactivated: newStatus ? prev.deactivated - 1 : prev.deactivated + 1,
      }));
      toast.success("User status updated");
    } catch (err) {
      console.error("Status toggle failed", err);
      toast.error("Failed to update user status");
    }
  };

  const handleDeleteUser = async (id) => {
    try {
      await deleteUser(id);
      setUsersData((prev) => prev.filter((u) => u._id !== id));
       // Update local stats
      setSummaryStats(prev => {
        const user = usersData.find(u => u._id === id);
        return {
          total: prev.total - 1,
          active: user.isActive ? prev.active - 1 : prev.active,
          deactivated: !user.isActive ? prev.deactivated - 1 : prev.deactivated,
        };
      });
      toast.success("User deleted successfully");
    } catch (err) {
      console.error("Delete failed", err);
      toast.error("Failed to delete user");
    }
  };

  const refreshData = () => {
    setLoading(true);
    getAllUsers(page, limit, search)
      .then(response => {
        const users = response.users || [];
        const activeUsers = response.activeUsers || users.filter((u) => u.isActive).length;
        const deactivatedUsers = response.deactivatedUsers || users.filter((u) => !u.isActive).length;
        
        setUsersData(users);
        setTotalPages(response.totalPages || 1);
        setSummaryStats({
          total: response.totalUsers || users.length,
          active: activeUsers,
          deactivated: deactivatedUsers,
        });
        toast.success("User data refreshed");
      })
      .catch(err => {
        console.error("Failed to refresh user data", err);
        toast.error("Failed to refresh user data");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="flex bg-gray-50 min-h-screen">
      <ToastContainer position="top-right" />

      {/* Mobile Sidebar */}
      <Sidebar
        visible={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onCollapsedChange={setSidebarCollapsed}
      />

      {/* Main content */}
      <div className={`flex-1 flex flex-col transition-all duration-300 pt-16`}>
        <Topbar
          onSidebarToggle={() => setSidebarOpen(true)}
          placeholder="Search users..."
          onSearchChange={handleSearchChange}
        />

        <main className="flex-1 p-4 md:p-6 pt-16">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">User Management</h1>
                <p className="mt-1 text-gray-600">
                  Manage and monitor all staff accounts in the ERP system
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={refreshData}
                  disabled={loading}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiRefreshCw className={`text-lg ${loading ? 'animate-spin' : ''}`} />
                  <span>Refresh Data</span>
                </button>
                
                {/* 👈 UPDATED: Calls modal instead of navigating */}
                <button
                  onClick={() => setShowAddUserModal(true)}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-md hover:shadow-lg"
                >
                  <FiPlus size={18} />
                  <span>Add New Staff</span>
                </button>
              </div>
            </div>
          </div>

          {/* Stats - Consistent Card UI */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 md:mb-8">
            <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-200">
              <div className="flex items-center">
                <div className="p-3 rounded-lg bg-orange-100 text-orange-600">
                  <FiUsers size={24} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Users</p>
                  <p className="text-2xl font-semibold text-gray-900">{loading ? '...' : summaryStats.total}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-200">
              <div className="flex items-center">
                <div className="p-3 rounded-lg bg-green-100 text-green-600">
                  <FiUserCheck size={24} /> {/* Updated Icon */}
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Active Users</p>
                  <p className="text-2xl font-semibold text-gray-900">{loading ? '...' : summaryStats.active}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-200">
              <div className="flex items-center">
                <div className="p-3 rounded-lg bg-red-100 text-red-600">
                  <FiUserX size={24} /> {/* Updated Icon */}
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Deactivated Users</p>
                  <p className="text-2xl font-semibold text-gray-900">{loading ? '...' : summaryStats.deactivated}</p>
                </div>
              </div>
            </div>
          </div>

          <UserTable
            users={usersData}
            loading={loading}
            setUsers={setUsersData}
            onRowClick={handleRowClick}
            onToggleStatus={handleToggleStatus}
            onDeleteUser={handleDeleteUser}
            pagination={{
              page,
              totalPages,
              onPageChange: (newPage) => setPage(newPage),
            }}
          />
        </main>
      </div>
      
      {/* 👈 CONDITIONAL MODAL RENDER */}
      {showAddUserModal && (
        <AddStaffModal
          closeForm={() => setShowAddUserModal(false)}
          onUserAdded={() => {
            setShowAddUserModal(false);
            // Refresh data to show the new user
            refreshData(); 
          }}
        />
      )}
    </div>
  );
};

export default UserListPage;